/**
 * Newsletter API Service
 * Clean, simple API integration for newsletter subscriptions
 * Follows KISS principle and existing patterns
 */

import { apiClient } from '../../../lib/api/client';
import { API_ENDPOINTS } from '../../../lib/api/config';
import type {
  NewsletterSubscribeRequest,
  NewsletterSubscribeResponse,
  NewsletterError,
} from '../types/newsletter.types';

/**
 * Subscribe to newsletter
 * @param email - Email address to subscribe
 * @returns Promise with subscription response
 * @throws NewsletterError on failure
 */
export async function subscribeNewsletter(
  email: string
): Promise<NewsletterSubscribeResponse> {
  try {
    // Validate email format before API call
    if (!email || typeof email !== 'string') {
      throw new Error('Email address is required');
    }

    const trimmedEmail = email.trim().toLowerCase();

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      throw new Error('Please enter a valid email address');
    }

    const requestData: NewsletterSubscribeRequest = {
      email: trimmedEmail,
    };

    const response = await apiClient.post<NewsletterSubscribeResponse['data']>(
      API_ENDPOINTS.newsletter.subscribe,
      requestData
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Successfully subscribed to newsletter',
        timestamp: response.timestamp,
      };
    }

    // Handle API error response
    throw new Error(response.message || 'Failed to subscribe to newsletter');
  } catch (error) {
    // Handle network errors
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        const networkError: NewsletterError = {
          code: 'NETWORK_ERROR',
          message: 'Network error - please check your connection and try again',
          timestamp: new Date().toISOString(),
        };
        throw networkError;
      }

      // Handle timeout errors
      if (error.message.includes('timeout')) {
        const timeoutError: NewsletterError = {
          code: 'NETWORK_ERROR',
          message: 'Request timeout - please try again',
          timestamp: new Date().toISOString(),
        };
        throw timeoutError;
      }

      // Re-throw if it's already a NewsletterError
      if ('code' in error && 'message' in error) {
        throw error;
      }

      // Convert generic errors to NewsletterError
      const newsletterError: NewsletterError = {
        code: 'SERVER_ERROR',
        message: error.message || 'An unexpected error occurred',
        timestamp: new Date().toISOString(),
      };
      throw newsletterError;
    }

    // Handle unknown error types
    const unknownError: NewsletterError = {
      code: 'SERVER_ERROR',
      message: 'An unexpected error occurred. Please try again later.',
      timestamp: new Date().toISOString(),
    };
    throw unknownError;
  }
}

/**
 * Handle API error responses and convert to NewsletterError
 * @param response - API response with error
 * @returns NewsletterError object
 */
export function handleNewsletterApiError(response: {
  success: false;
  message?: string;
  error?: string;
  statusCode?: number;
}): NewsletterError {
  const statusCode = response.statusCode || 500;
  let code: NewsletterError['code'] = 'SERVER_ERROR';
  let message = response.message || response.error || 'An error occurred';

  // Map HTTP status codes to error codes
  switch (statusCode) {
    case 400:
      code = 'VALIDATION_ERROR';
      message = message || 'Invalid email address format';
      break;
    case 409:
      code = 'ALREADY_SUBSCRIBED';
      message = message || 'This email is already subscribed to our newsletter';
      break;
    case 429:
      code = 'RATE_LIMIT_EXCEEDED';
      message = message || 'Too many requests. Please try again later.';
      break;
    case 500:
    case 502:
    case 503:
      code = 'SERVER_ERROR';
      message = message || 'Server error. Please try again later.';
      break;
    default:
      code = 'SERVER_ERROR';
      message = message || 'An unexpected error occurred';
  }

  return {
    code,
    message,
    timestamp: new Date().toISOString(),
  };
}

